import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import Navigation from './Navigation';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import './Dashboard.css';

interface Account {
  accountId: number;
  accountName: string;
  accountType: string;
  bankName?: string;
  currency: string;
  currentBalance: number;
  isDefault: boolean;
}

interface Transaction {
  transactionId: number;
  transactionName: string;
  amount: number;
  type: 'expense' | 'income';
  category: string;
  createdAt: string;
  transactionDate: string;
  userId: number;
  account?: Account;
  accountId?: number;
}

type DateRange = 'thisMonth' | 'last3Months' | 'custom';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const userId = localStorage.getItem('userId');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>('thisMonth');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const fetchTransactions = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await api.get(`/transactions?userId=${userId}`);
      if (!response.ok) throw new Error('Failed to fetch transactions');
      const data = await response.json();
      setTransactions(data);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const fetchAccounts = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await api.get(`/accounts/user/${userId}/active`);
      if (!response.ok) throw new Error('Failed to fetch accounts');
      const data = await response.json();
      setAccounts(data);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      navigate('/');
      return;
    }
    fetchTransactions();
    fetchAccounts();
  }, [userId, navigate, fetchTransactions, fetchAccounts]);

  // Calculate category-wise spending (expenses only)
  const getCategorySpending = () => {
    const categoryMap: { [key: string]: number } = {};
    const filtered = getFilteredTransactions();

    filtered.forEach(transaction => {
      if (transaction.type === 'expense') {
        const category = transaction.category || 'Other';
        categoryMap[category] = (categoryMap[category] || 0) + transaction.amount;
      }
    });

    return Object.entries(categoryMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };

  // Get top 5 spending categories
  const getTopCategories = () => {
    const categoryData = getCategorySpending();
    return categoryData.slice(0, 5).map(category => ({
      name: category.name,
      value: parseFloat(category.value.toFixed(3))
    }));
  };

  // Calculate category vs month spending for stacked bar chart (top 5 categories only)
  const getCategoryVsMonthSpending = () => {
    const filtered = getFilteredTransactions();
    const monthlyCategoryMap: { [key: string]: { [category: string]: number } } = {};
    const categoryTotals: { [category: string]: number } = {};

    // First pass: calculate totals per category
    filtered.forEach(transaction => {
      if (transaction.type === 'expense') {
        const category = transaction.category || 'Other';
        categoryTotals[category] = (categoryTotals[category] || 0) + transaction.amount;
      }
    });

    // Get top 5 categories by total spending
    const topCategories = Object.entries(categoryTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([category]) => category);

    // Second pass: process transactions (only include top 5 categories)
    filtered.forEach(transaction => {
      if (transaction.type === 'expense') {
        const transactionDate = transaction.transactionDate || transaction.createdAt.split('T')[0];
        const month = transactionDate.substring(0, 7); // YYYY-MM
        const category = transaction.category || 'Other';
        
        // Only include if it's in top 5 categories
        if (topCategories.includes(category)) {
          if (!monthlyCategoryMap[month]) {
            monthlyCategoryMap[month] = {};
          }

          if (!monthlyCategoryMap[month][category]) {
            monthlyCategoryMap[month][category] = 0;
          }

          monthlyCategoryMap[month][category] += transaction.amount;
        }
      }
    });

    // Convert to array format for stacked bar chart
    const result = Object.entries(monthlyCategoryMap)
      .map(([month, categoryData]) => {
        const monthLabel = new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        const dataPoint: { [key: string]: string | number } = { month: monthLabel };
        
        // Add only top 5 categories
        topCategories.forEach(category => {
          dataPoint[category] = parseFloat((categoryData[category] || 0).toFixed(3));
        });

        return {
          ...dataPoint,
          monthKey: month // For sorting
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.monthKey as string);
        const dateB = new Date(b.monthKey as string);
        return dateA.getTime() - dateB.getTime();
      })
      .map(({ monthKey, ...rest }) => rest); // Remove monthKey after sorting

    return {
      data: result,
      categories: topCategories
    };
  };

  // Calculate daily expenses with moving average
  const getDailyExpenseWithMovingAverage = (windowSize: number = 7) => {
    const dailyMap: { [key: string]: number } = {};
    const filtered = getFilteredTransactions();

    // Calculate daily expenses
    filtered.forEach(transaction => {
      if (transaction.type === 'expense') {
        const transactionDate = transaction.transactionDate || transaction.createdAt.split('T')[0];
        dailyMap[transactionDate] = (dailyMap[transactionDate] || 0) + transaction.amount;
      }
    });

    // Convert to array and sort by date
    const dailyData = Object.entries(dailyMap)
      .map(([date, value]) => ({
        date,
        value: parseFloat(value.toFixed(3)),
        timestamp: new Date(date).getTime()
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    if (dailyData.length === 0) return [];

    // Calculate moving average
    const result = dailyData.map((item, index) => {
      let movingAverage = 0;
      
      if (index >= windowSize - 1) {
        // Calculate average of previous windowSize days
        const window = dailyData.slice(index - windowSize + 1, index + 1);
        const sum = window.reduce((acc, curr) => acc + curr.value, 0);
        movingAverage = parseFloat((sum / windowSize).toFixed(3));
      } else {
        // For early days, calculate average of available days
        const window = dailyData.slice(0, index + 1);
        const sum = window.reduce((acc, curr) => acc + curr.value, 0);
        movingAverage = parseFloat((sum / (index + 1)).toFixed(3));
      }

      return {
        date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        actual: item.value,
        movingAverage: movingAverage,
        fullDate: item.date
      };
    });

    return result;
  };

  // Calculate monthly expense trend
  const getMonthlyExpenseTrend = () => {
    const monthlyMap: { [key: string]: number } = {};
    const filtered = getFilteredTransactions();

    filtered.forEach(transaction => {
      if (transaction.type === 'expense') {
        const transactionDate = transaction.transactionDate || transaction.createdAt.split('T')[0];
        const month = transactionDate.substring(0, 7); // YYYY-MM
        monthlyMap[month] = (monthlyMap[month] || 0) + transaction.amount;
      }
    });

    return Object.entries(monthlyMap)
      .map(([month, value]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        value: parseFloat(value.toFixed(3))
      }))
      .sort((a, b) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateA.getTime() - dateB.getTime();
      });
  };

  // Calculate monthly income vs expense
  const getMonthlyIncomeVsExpense = () => {
    const monthlyMap: { [key: string]: { income: number; expense: number } } = {};
    const filtered = getFilteredTransactions();

    filtered.forEach(transaction => {
      const transactionDate = transaction.transactionDate || transaction.createdAt.split('T')[0];
      const month = transactionDate.substring(0, 7); // YYYY-MM

      if (!monthlyMap[month]) {
        monthlyMap[month] = { income: 0, expense: 0 };
      }

      if (transaction.type === 'income') {
        monthlyMap[month].income += transaction.amount;
      } else {
        monthlyMap[month].expense += transaction.amount;
      }
    });

    return Object.entries(monthlyMap)
      .map(([month, data]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        income: parseFloat(data.income.toFixed(3)),
        expense: parseFloat(data.expense.toFixed(3))
      }))
      .sort((a, b) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateA.getTime() - dateB.getTime();
      });
  };

  // Calculate account-wise spending (expenses only)
  const getAccountWiseSpending = () => {
    const accountMap: { [key: number]: { name: string; value: number } } = {};
    const filtered = getFilteredTransactions();

    // Initialize all accounts with 0 spending
    accounts.forEach(account => {
      accountMap[account.accountId] = {
        name: account.accountName,
        value: 0
      };
    });

    // Calculate spending per account
    filtered.forEach(transaction => {
      if (transaction.type === 'expense' && transaction.accountId) {
        if (accountMap[transaction.accountId]) {
          accountMap[transaction.accountId].value += transaction.amount;
        } else {
          // Handle case where account might not be in the accounts list
          const accountName = transaction.account?.accountName || `Account ${transaction.accountId}`;
          accountMap[transaction.accountId] = {
            name: accountName,
            value: transaction.amount
          };
        }
      }
    });

    return Object.values(accountMap)
      .filter(account => account.value > 0) // Only show accounts with spending
      .map(account => ({
        name: account.name,
        value: parseFloat(account.value.toFixed(3))
      }))
      .sort((a, b) => b.value - a.value);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount) + ' KD';
  };

  // Get date range filter function
  const getDateRangeFilter = () => {
    const now = new Date();
    let startDate: string;
    let endDate: string = now.toISOString().split('T')[0];

    switch (dateRange) {
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        break;
      case 'last3Months':
        const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        startDate = threeMonthsAgo.toISOString().split('T')[0];
        break;
      case 'custom':
        startDate = customStartDate || '';
        endDate = customEndDate || endDate;
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    }

    return { startDate, endDate };
  };

  // Filter transactions based on date range
  const getFilteredTransactions = () => {
    const { startDate, endDate } = getDateRangeFilter();
    if (!startDate && !endDate) return transactions;

    return transactions.filter(transaction => {
      const transactionDate = transaction.transactionDate || transaction.createdAt.split('T')[0];
      if (startDate && transactionDate < startDate) return false;
      if (endDate && transactionDate > endDate) return false;
      return true;
    });
  };

  // Calculate KPIs
  const calculateKPIs = () => {
    const filtered = getFilteredTransactions();
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const thisMonthEnd = now.toISOString().split('T')[0];

    // Total Balance (sum of all account balances)
    const totalBalance = accounts.reduce((sum, account) => sum + account.currentBalance, 0);

    // This Month Income
    const thisMonthIncome = filtered
      .filter(t => {
        const transactionDate = t.transactionDate || t.createdAt.split('T')[0];
        return t.type === 'income' && transactionDate >= thisMonthStart && transactionDate <= thisMonthEnd;
      })
      .reduce((sum, t) => sum + t.amount, 0);

    // This Month Expense
    const thisMonthExpense = filtered
      .filter(t => {
        const transactionDate = t.transactionDate || t.createdAt.split('T')[0];
        return t.type === 'expense' && transactionDate >= thisMonthStart && transactionDate <= thisMonthEnd;
      })
      .reduce((sum, t) => sum + t.amount, 0);

    // Savings Rate (%)
    const savingsRate = thisMonthIncome > 0 
      ? ((thisMonthIncome - thisMonthExpense) / thisMonthIncome) * 100 
      : 0;

    return {
      totalBalance,
      thisMonthIncome,
      thisMonthExpense,
      savingsRate: Math.max(0, savingsRate) // Ensure non-negative
    };
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ zIndex: 10000, position: 'relative' }}>
          <p className="tooltip-label">{payload[0].name || payload[0].dataKey}</p>
          <p className="tooltip-value">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for stacked bar chart showing all categories
  const StackedBarTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);
      return (
        <div className="custom-tooltip" style={{ zIndex: 10000, position: 'relative', minWidth: '200px' }}>
          <p className="tooltip-label" style={{ marginBottom: '12px', fontWeight: 600, fontSize: '14px' }}>
            {label}
          </p>
          {payload
            .filter((entry: any) => entry.value > 0)
            .sort((a: any, b: any) => b.value - a.value)
            .map((entry: any, index: number) => (
              <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      backgroundColor: entry.color,
                    }}
                  />
                  <span className="tooltip-label" style={{ fontSize: '13px' }}>{entry.name}:</span>
                </div>
                <span className="tooltip-value" style={{ fontSize: '13px', fontWeight: 600 }}>
                  {formatCurrency(entry.value)}
                </span>
              </div>
            ))}
          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="tooltip-label" style={{ fontSize: '13px', fontWeight: 600 }}>Total:</span>
              <span className="tooltip-value" style={{ fontSize: '14px', fontWeight: 700 }}>
                {formatCurrency(total)}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  const categoryData = getCategorySpending();
  const monthlyExpenseData = getMonthlyExpenseTrend();
  const incomeVsExpenseData = getMonthlyIncomeVsExpense();
  const accountSpendingData = getAccountWiseSpending();
  const topCategoriesData = getTopCategories();
  const dailyExpense30Day = getDailyExpenseWithMovingAverage(30);
  const categoryVsMonthData = getCategoryVsMonthSpending();
  const kpis = calculateKPIs();

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1 className="dashboard-title">📊 Dashboard</h1>
          
          {/* Date Range Filter */}
          <div className="date-range-filter">
            <button
              className={`date-range-btn ${dateRange === 'thisMonth' ? 'active' : ''}`}
              onClick={() => setDateRange('thisMonth')}
            >
              This Month
            </button>
            <button
              className={`date-range-btn ${dateRange === 'last3Months' ? 'active' : ''}`}
              onClick={() => setDateRange('last3Months')}
            >
              Last 3 Months
            </button>
            <button
              className={`date-range-btn ${dateRange === 'custom' ? 'active' : ''}`}
              onClick={() => setDateRange('custom')}
            >
              Custom Range
            </button>
            {dateRange === 'custom' && (
              <div className="custom-date-inputs">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  placeholder="Start Date"
                  className="date-input"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  placeholder="End Date"
                  className="date-input"
                />
              </div>
            )}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* KPI Summary Cards */}
        <div className="kpi-grid">
          <div className="kpi-card kpi-balance">
            <div className="kpi-icon">💰</div>
            <div className="kpi-content">
              <p className="kpi-label">Total Balance</p>
              <h3 className="kpi-value">{formatCurrency(kpis.totalBalance)}</h3>
            </div>
          </div>
          <div className="kpi-card kpi-income">
            <div className="kpi-icon">📈</div>
            <div className="kpi-content">
              <p className="kpi-label">This Month Income</p>
              <h3 className="kpi-value">{formatCurrency(kpis.thisMonthIncome)}</h3>
            </div>
          </div>
          <div className="kpi-card kpi-expense">
            <div className="kpi-icon">📉</div>
            <div className="kpi-content">
              <p className="kpi-label">This Month Expense</p>
              <h3 className="kpi-value">{formatCurrency(kpis.thisMonthExpense)}</h3>
            </div>
          </div>
          <div className="kpi-card kpi-savings">
            <div className="kpi-icon">💎</div>
            <div className="kpi-content">
              <p className="kpi-label">Savings Rate</p>
              <h3 className="kpi-value">{kpis.savingsRate.toFixed(1)}%</h3>
            </div>
          </div>
        </div>

        <div className="charts-grid">
          {/* Pie Chart - Category-wise Spending */}
          <div className="chart-card">
            <h2 className="chart-title">
              <span className="chart-icon">📊</span>
              Category-wise Spending
            </h2>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={450}>
                <PieChart margin={{ top: 10, right: 10, bottom: 100, left: 10 }}>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="45%"
                    labelLine={false}
                    label={false}
                    outerRadius={85}
                    fill="#8884d8"
                    dataKey="value"
                    stroke={theme === 'dark' ? '#1f2937' : '#ffffff'}
                    strokeWidth={2}
                  >
                    {categoryData.map((entry, index) => {
                      // Use diverse, colorful palette for categories
                      const categoryColors = [
                        '#dc2626', // Red - Food
                        '#2563eb', // Blue - Shopping
                        '#059669', // Green - Bills
                        '#7c3aed', // Purple - Entertainment
                        '#ea580c', // Orange - Healthcare
                        '#0891b2', // Cyan - Education
                        '#be185d', // Pink - Other
                        '#ca8a04', // Yellow - Travel
                        '#1e40af', // Dark Blue - Utilities
                        '#16a34a', // Light Green - Groceries
                        '#9333ea', // Violet - Subscriptions
                        '#c2410c'  // Dark Orange - Transport
                      ];
                      return <Cell key={`cell-${index}`} fill={categoryColors[index % categoryColors.length]} />;
                    })}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={90}
                    iconType="circle"
                    wrapperStyle={{ paddingTop: '20px' }}
                    formatter={(value) => {
                      return <span style={{ color: theme === 'dark' ? '#e5e7eb' : '#0f172a', fontSize: '13px', fontWeight: '500' }}>{value}</span>;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">📊</div>
                <p>No expense data available</p>
                <p className="no-data-subtitle">Add transactions to see category breakdown</p>
              </div>
            )}
          </div>

          {/* Donut Chart - Account-wise Spending */}
          <div className="chart-card">
            <h2 className="chart-title">
              <span className="chart-icon">🏦</span>
              Account-wise Spending
            </h2>
            {accountSpendingData.length > 0 ? (
              <ResponsiveContainer width="100%" height={450}>
                <PieChart>
                  <Pie
                    data={accountSpendingData}
                    cx="50%"
                    cy="45%"
                    labelLine={false}
                    label={false}
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {accountSpendingData.map((entry, index) => {
                      // Use refined blue tones for account spending (professional fintech palette)
                      const accountColors = ['#1e40af', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe'];
                      return <Cell key={`cell-${index}`} fill={accountColors[index % accountColors.length]} />;
                    })}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">🏦</div>
                <p>No expense data available for accounts</p>
                <p className="no-data-subtitle">Add transactions to see account breakdown</p>
              </div>
            )}
          </div>

          {/* Line Chart - Monthly Expense Trend */}
          <div className="chart-card">
            <h2 className="chart-title">
              <span className="chart-icon">📅</span>
              Monthly Expense Trend
            </h2>
            {monthlyExpenseData.length > 0 ? (
              <ResponsiveContainer width="100%" height={450}>
                <LineChart data={monthlyExpenseData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" height={36} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#dc2626"
                    strokeWidth={3}
                    name="Expense"
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">📅</div>
                <p>No expense data available</p>
                <p className="no-data-subtitle">Add transactions to see monthly trends</p>
              </div>
            )}
          </div>

          {/* Horizontal Bar Chart - Top 5 Categories */}
          <div className="chart-card">
            <h2 className="chart-title">
              <span className="chart-icon">🏆</span>
              Top 5 Spending Categories
            </h2>
            {topCategoriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={topCategoriesData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                    {topCategoriesData.map((entry: { name: string; value: number }, index: number) => {
                      // Use refined red tones for expense categories (professional fintech palette)
                      const expenseColors = ['#dc2626', '#ef4444', '#f87171', '#fb7185', '#fca5a5'];
                      return <Cell key={`cell-${index}`} fill={expenseColors[index % expenseColors.length]} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">🏆</div>
                <p>No expense data available</p>
                <p className="no-data-subtitle">Add transactions to see top categories</p>
              </div>
            )}
          </div>

          {/* Stacked Bar Chart - Category vs Month */}
          <div className="chart-card">
            <h2 className="chart-title">
              <span className="chart-icon">📊</span>
              Category vs Month Spending
            </h2>
            {categoryVsMonthData.data.length > 0 && categoryVsMonthData.categories.length > 0 ? (
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={categoryVsMonthData.data} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip content={<StackedBarTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={90}
                    iconType="circle"
                    wrapperStyle={{ paddingTop: '20px' }}
                    formatter={(value) => {
                      return <span style={{ color: theme === 'dark' ? '#e5e7eb' : '#0f172a', fontSize: '13px', fontWeight: '500' }}>{value}</span>;
                    }}
                  />
                  {categoryVsMonthData.categories.map((category, index) => {
                    // Use same color palette as Category-wise Spending
                    const categoryColors = [
                      '#dc2626', // Red - Food
                      '#2563eb', // Blue - Shopping
                      '#059669', // Green - Bills
                      '#7c3aed', // Purple - Entertainment
                      '#ea580c', // Orange - Healthcare
                      '#0891b2', // Cyan - Education
                      '#be185d', // Pink - Other
                      '#ca8a04', // Yellow - Travel
                      '#1e40af', // Dark Blue - Utilities
                      '#16a34a', // Light Green - Groceries
                      '#9333ea', // Violet - Subscriptions
                      '#c2410c'  // Dark Orange - Transport
                    ];
                    return (
                      <Bar 
                        key={category} 
                        dataKey={category} 
                        stackId="a" 
                        fill={categoryColors[index % categoryColors.length]}
                        name={category}
                        radius={index === categoryVsMonthData.categories.length - 1 ? [0, 8, 8, 0] : [0, 0, 0, 0]}
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">📊</div>
                <p>No expense data available</p>
                <p className="no-data-subtitle">Add transactions to see category breakdown by month</p>
              </div>
            )}
          </div>

          {/* Moving Average Line Chart - 30-Day */}
          <div className="chart-card">
            <h2 className="chart-title">
              <span className="chart-icon">📈</span>
              Daily Expense Trend (30-Day Moving Average)
            </h2>
            {dailyExpense30Day.length > 0 ? (
              <ResponsiveContainer width="100%" height={450}>
                <LineChart data={dailyExpense30Day}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval="preserveStartEnd"
                  />
                  <YAxis />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="custom-tooltip" style={{ zIndex: 10000, position: 'relative' }}>
                            <p className="tooltip-label">{payload[0].payload.fullDate}</p>
                            <p className="tooltip-value">Actual: {formatCurrency(payload[0].payload.actual)}</p>
                            <p className="tooltip-value">30-Day Avg: {formatCurrency(payload[0].payload.movingAverage)}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend verticalAlign="bottom" height={36} />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke="#dc2626"
                    strokeWidth={2}
                    name="Daily Expense"
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="movingAverage"
                    stroke="#7c3aed"
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    name="30-Day Moving Average"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">📈</div>
                <p>No expense data available</p>
                <p className="no-data-subtitle">Add transactions to see 30-day trends</p>
              </div>
            )}
          </div>

          {/* Bar Chart - Income vs Expense */}
          <div className="chart-card full-width">
            <h2 className="chart-title">
              <span className="chart-icon">⚖️</span>
              Monthly Income vs Expense
            </h2>
            {incomeVsExpenseData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={incomeVsExpenseData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" height={36} />
                  <Bar dataKey="income" fill="#059669" name="Income" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="expense" fill="#dc2626" name="Expense" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">
                <div className="no-data-icon">⚖️</div>
                <p>No transaction data available</p>
                <p className="no-data-subtitle">Add transactions to see income vs expense comparison</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

